if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();

      this.isLoading = false;
      this.observer = null;
      this.abortController = null;

      this.handleButtonClick = this.handleButtonClick.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.gridSelector = this.dataset.gridSelector || '#product-grid';

      this.button = this.querySelector(
        '.collection-infinite-scroll__button'
      );

      this.spinner = this.querySelector(
        '.collection-infinite-scroll__spinner'
      );

      this.endMessage = this.querySelector(
        '.collection-infinite-scroll__end'
      );

      this.errorMessage = this.querySelector(
        '.collection-infinite-scroll__error'
      );

      this.sentinel = this.querySelector(
        '.collection-infinite-scroll__sentinel'
      );

      this.button?.addEventListener('click', this.handleButtonClick);
      this.updateState();

      if (!this.nextUrl) return;
      this.createObserver();
    }

    disconnectedCallback() {
      this.button?.removeEventListener(
        'click',
        this.handleButtonClick
      );

      this.observer?.disconnect();
      this.abortController?.abort();
    }

    get nextUrl() {
      return this.dataset.nextUrl || '';
    }

    set nextUrl(url) {
      if (url) {
        this.dataset.nextUrl = url;
      } else {
        delete this.dataset.nextUrl;
      }
    }

    createObserver() {
      if (!this.sentinel || !('IntersectionObserver' in window)) {
        return;
      }

      this.observer?.disconnect();

      this.observer = new IntersectionObserver(
        this.handleIntersection,
        {
          rootMargin: '600px 0px',
          threshold: 0
        }
      );

      this.observer.observe(this.sentinel);
    }

    handleIntersection(entries) {
      const shouldLoad = entries.some((entry) => {
        return entry.isIntersecting;
      });

      if (shouldLoad) {
        this.loadNextPage();
      }
    }

    handleButtonClick() {
      this.loadNextPage();
    }

    async loadNextPage() {
      if (this.isLoading || !this.nextUrl) return;

      const currentNextUrl = this.nextUrl;

      this.setLoading(true);
      this.hideError();

      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const requestUrl = this.buildSectionUrl(currentNextUrl);

        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(
            `Infinite scroll request failed: ${response.status}`
          );
        }

        const responseText = await response.text();
        const parser = new DOMParser();
        const responseDocument = parser.parseFromString(
          responseText,
          'text/html'
        );

        const currentGrid = document.querySelector(this.gridSelector);
        const nextGrid = responseDocument.querySelector(
          this.gridSelector
        );

        if (!currentGrid || !nextGrid) {
          throw new Error('Product grid could not be found.');
        }

        const newItems = Array.from(
          nextGrid.querySelectorAll(':scope > .grid__item')
        );

        if (!newItems.length) {
          this.nextUrl = '';
          this.updateState();
          return;
        }

        const fragment = document.createDocumentFragment();

        newItems.forEach((item) => {
          this.prepareProductItem(item, currentGrid);
          fragment.appendChild(item);
        });

        currentGrid.appendChild(fragment);

        const nextInfiniteScroll =
          responseDocument.querySelector('infinite-scroll');

        this.nextUrl =
          nextInfiniteScroll?.dataset.nextUrl || '';

        this.updateState();
        this.reinitialiseThemeFeatures(newItems);
        this.dispatchProductsLoaded(newItems, currentNextUrl);
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('[Infinite scroll]', error);
        this.showError();
      } finally {
        this.setLoading(false);
      }
    }

    buildSectionUrl(nextUrl) {
      const url = new URL(nextUrl, window.location.origin);

      if (this.sectionId) {
        url.searchParams.set('section_id', this.sectionId);
      }

      return url.toString();
    }

    prepareProductItem(item, currentGrid) {
      /*
       * Animation order restarts on every paginated response.
       * Update it so newly appended products continue the sequence.
       */
      const currentItemCount = currentGrid.querySelectorAll(
        ':scope > .grid__item'
      ).length;

      const newOrder = currentItemCount + 1;

      if (item.hasAttribute('data-cascade')) {
        item.style.setProperty('--animation-order', newOrder);
      }
    }

    reinitialiseThemeFeatures(newItems) {
      /*
       * Most Shopify theme components, including custom elements used
       * by quick add, initialise automatically when inserted into the DOM.
       *
       * This event is available for any custom theme or app code that
       * needs to initialise manually.
       */
      document.dispatchEvent(
        new CustomEvent('collection:products-added', {
          detail: {
            items: newItems,
            sectionId: this.sectionId
          }
        })
      );

      /*
       * Dawn-style scroll animations sometimes require visibility classes
       * when content is inserted after initial page load.
       */
      newItems.forEach((item) => {
        if (item.classList.contains('scroll-trigger')) {
          requestAnimationFrame(() => {
            item.classList.add('scroll-trigger--visible');
          });
        }
      });
    }

    dispatchProductsLoaded(items, pageUrl) {
      this.dispatchEvent(
        new CustomEvent('infinite-scroll:loaded', {
          bubbles: true,
          detail: {
            items,
            pageUrl,
            sectionId: this.sectionId
          }
        })
      );
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;

      if (this.spinner) {
        this.spinner.hidden = !isLoading;
      }

      if (this.button) {
        this.button.disabled = isLoading;
        this.button.setAttribute(
          'aria-busy',
          String(isLoading)
        );
      }

      this.toggleAttribute('loading', isLoading);
    }

    updateState() {
      const hasNextPage = Boolean(this.nextUrl);
      const status = this.querySelector(
        '.collection-infinite-scroll__status'
      );

      if (status) {
        status.hidden = !hasNextPage;
      }

      if (this.button) {
        this.button.hidden = !hasNextPage;
      }

      if (!hasNextPage) {
        this.observer?.disconnect();
      }
    }

    showError() {
      if (this.errorMessage) {
        this.errorMessage.hidden = false;
      }

      /*
       * Keep the button visible so the customer can retry manually.
       */
      if (this.button && this.nextUrl) {
        this.button.hidden = false;
      }
    }

    hideError() {
      if (this.errorMessage) {
        this.errorMessage.hidden = true;
      }
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}